import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Card, Table, useTableColumns, } from '@wit-support/ui';
import { EntityId } from '@wit-support/ui-domain';
import { discrepancyRawTitle, discrepancyValueRu } from '../discrepancy-values.js';
import { metaObjectKind, objectLabel } from '../naming.js';
import { ColumnHeader, sortRows, useColumnSort } from './table-kit.js';
function ValueCell({ column, value, desc, }) {
    const shown = discrepancyValueRu(value, desc);
    const title = discrepancyRawTitle(value, shown);
    return (_jsx(Table.Cell, { column: column, className: "text-sm [overflow-wrap:anywhere]", title: title, children: shown }));
}
/** Справочная таблица на пять колонок: меню «Колонки» нет, закрепление и сортировка — есть. */
const COLUMNS = [
    { id: 'field', label: 'Поле', hideable: false, defaultPin: 'left' },
    { id: 'object', label: 'Объект' },
    { id: 'expected', label: 'Ожидали' },
    { id: 'actual', label: 'Получили' },
    { id: 'severity', label: 'Важность' },
];
function discrepancySortValue(d, key, fields) {
    const desc = d.fieldKey != null ? (fields?.get(d.fieldKey) ?? null) : null;
    switch (key) {
        case 'field':
            return d.label !== '' ? d.label : d.path;
        case 'object':
            return objectLabel(d.kind, d.localRef);
        case 'expected':
            return discrepancyValueRu(d.expected, desc);
        case 'actual':
            return discrepancyValueRu(d.actual, desc);
        case 'severity':
            return d.severity === 'error' ? 0 : 1;
    }
}
function DiscrepancyCell({ column: c, d, desc, }) {
    switch (c.id) {
        case 'field':
            return (_jsx(Table.Cell, { column: c, children: _jsxs("div", { className: "flex flex-col", children: [_jsx("span", { className: "font-medium", children: d.label !== '' ? d.label : d.path }), d.label !== '' ? (_jsx("span", { className: "font-mono text-xs text-[var(--color-text-muted)]", children: d.path })) : null] }) }));
        case 'object':
            return (_jsx(Table.Cell, { column: c, className: "text-xs", title: `${d.kind} ${d.localRef}`, children: _jsxs("span", { className: "group/entity inline-flex flex-wrap items-center gap-x-1", children: [objectLabel(d.kind, d.localRef), d.metaId != null ? _jsx(EntityId, { id: d.metaId, kind: metaObjectKind(d.kind) }) : null] }) }));
        case 'expected':
            return _jsx(ValueCell, { column: c, value: d.expected, desc: desc });
        case 'actual':
            return _jsx(ValueCell, { column: c, value: d.actual, desc: desc });
        case 'severity':
            return (_jsx(Table.Cell, { column: c, children: _jsx(Badge, { variant: d.severity === 'error' ? 'danger' : 'warning', children: d.severity === 'error' ? 'ошибка' : 'предупреждение' }) }));
    }
}
/**
 * Таблица сверки «Поле (RU) · Ожидали · Получили · Важность». Подпись — из
 * реестра; пустая (старый запуск) — путь в объекте Meta. Значения — словами
 * по полю реестра (`fields`): 0/1 и OPT_IN/OPT_OUT → «вкл./выкл.».
 * Сортировка по любой колонке — все расхождения запуска на экране.
 */
export function DiscrepancyTable({ items, fields, }) {
    const cols = useTableColumns(null, COLUMNS);
    const [sort, setSort] = useColumnSort(cols);
    // ключ строки — позиция в исходном списке: она не меняется от сортировки
    const rows = sortRows(items.map((d, i) => ({ d, i })), sort, (r, key) => discrepancySortValue(r.d, key, fields));
    return (_jsx(Card, { className: "overflow-hidden", children: _jsxs(Table, { density: "compact", "data-testid": "discrepancies", children: [_jsx(Table.Header, { children: _jsx(Table.Row, { children: cols.visible.map((c) => (_jsx(ColumnHeader, { column: c, sort: sort, onSortChange: setSort }, c.id))) }) }), _jsx(Table.Body, { children: rows.map(({ d, i }) => {
                        const desc = d.fieldKey != null ? (fields?.get(d.fieldKey) ?? null) : null;
                        return (_jsx(Table.Row, { children: cols.visible.map((c) => (_jsx(DiscrepancyCell, { column: c, d: d, desc: desc }, c.id))) }, `${d.localRef}-${d.path}-${i}`));
                    }) })] }) }));
}
//# sourceMappingURL=discrepancy-table.js.map